import ApiResponseV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/ApiResponseV2";
import { IPageV2 } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/IPageV2";
import Tenant from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/Tenant";
import GenericCrudServiceV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/GenericCrudServiceV2";
import HttpClientV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/HttpClientV2";
import { ActivityOnboarding } from "@/interfaces/onboarding-interfaces/ActivityOnboarding";
import { CompanyOnboarding } from "@/interfaces/onboarding-interfaces/CompanyOnboarding";
import { CompanySeats } from "@/models/CompanySeats";
import { LazoCompany, SimpleUser } from "@/models/LazoCompany";
import { CompanyFilterOption } from "@/models/SearchAndReportingCompany";
import { LazoCompanyFilters } from "@/types/filters/LazoCompanyFilters";
import { CompleteRegistrationRequestDTO, RegistrationPreviewDTO } from "@/types/RegistrationTypes";
import { RequiredProfileInformationDTO } from "@/types/RequiredProfileInformationDTO";

class LazoCompanyService extends GenericCrudServiceV2<LazoCompany> {
  path: string;
  constructor(path: string) {
    super(path);
    this.path = path;
  }

  getAllCompaniesByUserExternalId(userExternalId?: string): Promise<ApiResponseV2<LazoCompany[]>> {
    const params = userExternalId ? `?userExternalId=${userExternalId}` : "";
    return HttpClientV2.get(`${this.path}/all-by-user${params}`);
  }

  async getAllCompaniesByOwnerExternalId(userExternalId: string): Promise<ApiResponseV2<LazoCompany[]>> {
    return HttpClientV2.get(`${this.path}/owner/${userExternalId}`);
  }

  getByTenantExternalId(tenantExternalId: string): Promise<ApiResponseV2<LazoCompany>> {
    return HttpClientV2.get(`${this.path}/tenant?tenantExternalId=${tenantExternalId}`);
  }

  async getSeatsInfo(companyExternalId: string): Promise<ApiResponseV2<CompanySeats>> {
    return HttpClientV2.get(`${this.path}/seats/${companyExternalId}`);
  }

  getActivitySurvey(tenantExternalId: string): Promise<ApiResponseV2<ActivityOnboarding>> {
    return HttpClientV2.get(`${this.path}/one/activity-survey?tenantExternalId=${tenantExternalId}`);
  }

  createCompany(userExternalId: string, company: CompanyOnboarding): Promise<ApiResponseV2<string>> {
    return HttpClientV2.post(`${this.path}?lazoUserExternalId=${userExternalId}`, company);
  }

  getCompaniesTenantData(): Promise<ApiResponseV2<Tenant[]>> {
    return HttpClientV2.get(`${this.path}/all`);
  }

  assignKam(companyExternalId: string, userExternalId: string): Promise<ApiResponseV2<SimpleUser>> {
    const queryParams = new URLSearchParams();
    queryParams.set("companyExternalId", companyExternalId);
    if (userExternalId) {
      queryParams.set("userExternalId", userExternalId);
    }

    return HttpClientV2.put(`${this.path}/assign-kam`, {}, { axiosConfig: { params: queryParams } });
  }

  updateGeneralInfo(companyExternalId: string, dto: LazoCompany, year: number): Promise<ApiResponseV2<LazoCompany>> {
    const params = new URLSearchParams({ year: year.toString() });

    return HttpClientV2.put(`${this.path}/portal/general/${companyExternalId}`, dto, { axiosConfig: { params } });
  }

  updateCompanyOnTask(dto: LazoCompany): Promise<ApiResponseV2<LazoCompany>> {
    return HttpClientV2.put(`${this.path}/from-task/${dto.externalId}`, dto);
  }

  /* ADMIN TOOL */
  search(search: string): Promise<ApiResponseV2<LazoCompany[]>> {
    return HttpClientV2.get(`${this.path}/search?search=${search}`);
  }

  removeFromCompany(coreUserExternalIds: string[], tenantExternalId: string): Promise<ApiResponseV2<string>> {
    return HttpClientV2.post(`${this.path}/remove/users?tenantExternalId=${tenantExternalId}`, {
      values: coreUserExternalIds,
    });
  }

  getByTerm(term: string): Promise<ApiResponseV2<Partial<LazoCompany>[]>> {
    return HttpClientV2.get(`${this.path}/by-term?term=${term}`);
  }

  getOnboardingCompanies(filters: LazoCompanyFilters, page: number, pageSize: number = 20): Promise<ApiResponseV2<IPageV2<LazoCompany>>> {
    const params = new URLSearchParams();
    params.set("page", page.toString());
    params.set("pageSize", pageSize.toString());

    return HttpClientV2.post(`${this.path}/onboarding`, filters, { axiosConfig: { params } });
  }

  getRequiredCompanyInformation(companyExternalId: string): Promise<ApiResponseV2<RequiredProfileInformationDTO>> {
    return HttpClientV2.get(`${this.path}/${companyExternalId}/required-profile-information`);
  }

  completeCompanyInformation(companyExternalId: string, data: RequiredProfileInformationDTO): Promise<ApiResponseV2<void>> {
    return HttpClientV2.post(`${this.path}/${companyExternalId}/required-profile-information`, data);
  }

  updateOnboardingStatus(companyExternalId: string, status: string): Promise<ApiResponseV2<void>> {
    const queryParams = new URLSearchParams({
      status,
    });

    return HttpClientV2.patchWithoutPayload(`${this.path}/${companyExternalId}/onboarding-status`, {
      axiosConfig: { params: queryParams },
    });
  }

  getRegistrationLink(companyExternalId: string): Promise<ApiResponseV2<string>> {
    return HttpClientV2.get(`${this.path}/${companyExternalId}/registration-link`);
  }

  getCompanyHistory(companyExternalId: string, year: number): Promise<ApiResponseV2<LazoCompany>> {
    const params = new URLSearchParams({ year: year.toString() });

    return HttpClientV2.get(`${this.path}/${companyExternalId}/history`, { axiosConfig: { params } });
  }

  getCompaniesForFilter(): Promise<ApiResponseV2<CompanyFilterOption[]>> {
    return HttpClientV2.post(`${this.path}/for-filter`, {});
  }

  getCompaniesByOrderNumber(ignitionOrderId: string): Promise<ApiResponseV2<LazoCompany[]>> {
    const params = new URLSearchParams({ ignitionOrderId });
    return HttpClientV2.get(`${this.path}/by-order-number`, { axiosConfig: { params } });
  }

  generate360Report(companyExternalId: string): Promise<ApiResponseV2<string>> {
    return HttpClientV2.post(`${this.path}/${companyExternalId}/360-report`, {});
  }

  getWorkspaceBaseUrl(tenantExternalId: string): Promise<ApiResponseV2<string>> {
    return HttpClientV2.get(`${this.path}/tenant/${tenantExternalId}/workspace-base-url`);
  }

  getRegistrationPreview(token: string): Promise<ApiResponseV2<RegistrationPreviewDTO>> {
    return HttpClientV2.get(`${this.path}/public/registration-preview?token=${encodeURIComponent(token)}`);
  }

  completeRegistration(dto: CompleteRegistrationRequestDTO): Promise<ApiResponseV2<string>> {
    return HttpClientV2.post(`${this.path}/public/complete-registration`, dto);
  }
}

export default new LazoCompanyService("/api/lazo-company");
